// src/pages/educator/EducatorClassroom.js
//
// ONE classroom for every state and grade.
//
// ===========================================================================
// WHAT THIS REPLACES, AND WHY
// ===========================================================================
// There were 36 near-identical files under pages/educator/{state}/, each
// ~576 lines, differing only in a state token, a grade token, and a page
// title. Every bug below had to be fixed 36 times, so in practice it was
// fixed once and the other 35 drifted.
//
// The bugs, all of which are fixed here:
//
//   1. ROSTER MISSED STUDENTS.
//      fetchStudents ran where("role","==","va-3rd-student") — an exact
//      match. A student whose role was "student", or "va-4th-student"
//      because they were set up a grade off, or a seat-claimed role written
//      by claimSeat, was invisible to their own teacher. The roster now
//      queries on educatorCode ALONE, which is the only field that actually
//      defines class membership, and derives grade for display instead of
//      filtering on it.
//
//   2. ONLY SCIENCE WAS EVER SHOWN.
//      The progress table did:
//          topKeys.find(k => normalize(k).includes("_science"))
//      Math, social studies and "other" progress was being collected the
//      whole time and silently never rendered. All subjects show now, as
//      tabs.
//
//   3. DEBUG OUTPUT RENDERED TO TEACHERS.
//      A <DebugKeysRow> printed raw Firestore keys into the table, and
//      handleStudentClick console.logged the student's entire profile under
//      "== RULES DEBUG ==". Both are gone.
//
//   4. BROKEN AVATAR IMAGES.
//      The detail panel rendered <img src={student.avatarUrl}> with no guard,
//      so every student without an avatar got a broken-image icon. There's a
//      generated initials fallback now.
//
// ===========================================================================
// WHERE THE DATA COMES FROM
// ===========================================================================
// Two sources, merged, because both exist and neither is complete alone:
//
//   userData/{studentUid}
//     Written by the student's own client. Authoritative, but only readable
//     by an educator when firestore.rules permits it (see rules note below).
//
//   classroomProgress/{educatorUid}/subjects/{subjectKey}/students/{uid}
//     The mirror. Written by the student, read by the educator, and the
//     path is owned by the educator so the rules are simple. This is the one
//     that was silently empty for most teachers until the lookup fix in
//     utils/resolveEducatorUid.js.
//
// Reading the mirror first and falling back to userData means the classroom
// keeps working for students whose progress predates the mirror.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getFirestore,
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";
import { listRoster } from "../../utils/educatorCode";
import FocusAvatar, { clearFocusedAvatar } from "../../components/avatar/FocusAvatar";
import ClassGiveCoins from "../../components/coins/ClassGiveCoins";
import CoinCounter from "../../components/coins/CoinCounter";
import BuyCoinsButton from "../../components/coins/BuyCoinsButton";
import { formalName } from "../../utils/displayName";
import "./classroom.css";

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const SUBJECTS = [
  { key: "math", label: "Math" },
  { key: "science", label: "Science" },
  { key: "socialstudies", label: "Social Studies" },
];
const SUBJECT_ICONS = { math: "🔢", science: "🔬", socialstudies: "🌎" };

const STATE_NAMES = {
  al: "Alabama", ak: "Alaska", az: "Arizona", ar: "Arkansas", ca: "California",
  co: "Colorado", ct: "Connecticut", de: "Delaware", dc: "District of Columbia",
  fl: "Florida", ga: "Georgia", hi: "Hawaii", id: "Idaho", il: "Illinois",
  in: "Indiana", ia: "Iowa", ks: "Kansas", ky: "Kentucky", la: "Louisiana",
  me: "Maine", md: "Maryland", ma: "Massachusetts", mi: "Michigan",
  mn: "Minnesota", ms: "Mississippi", mo: "Missouri", mt: "Montana",
  ne: "Nebraska", nv: "Nevada", nh: "New Hampshire", nj: "New Jersey",
  nm: "New Mexico", ny: "New York", nc: "North Carolina", nd: "North Dakota",
  oh: "Ohio", ok: "Oklahoma", or: "Oregon", pa: "Pennsylvania",
  ri: "Rhode Island", sc: "South Carolina", sd: "South Dakota",
  tn: "Tennessee", tx: "Texas", ut: "Utah", vt: "Vermont", va: "Virginia",
  wa: "Washington", wv: "West Virginia", wi: "Wisconsin", wy: "Wyoming",
};

const titleCase = (s = "") =>
  String(s).replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

const normalizeGrade = (raw = "") => {
  const s = String(raw).toLowerCase();
  const m = s.match(/\b(k|prek|pre-k|[0-9]+(?:st|nd|rd|th))\b/);
  return m ? m[1].replace("pre-k", "prek") : "";
};

// ---------------------------------------------------------------------------

export default function EducatorClassroom() {
  const db = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();
  const params = useParams();

  const [educator, setEducator] = useState(null);
  const [students, setStudents] = useState([]);
  const [progressByStudent, setProgressByStudent] = useState({});
  const [selectedUid, setSelectedUid] = useState(null);
  const [picked, setPicked] = useState([]); // ticked for Give coins
  const [subject, setSubject] = useState("math");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [breakdown, setBreakdown] = useState(null);

  // State and grade come from the route when present, and fall back to the
  // educator's own profile. The old pages hardcoded both.
  const routeState = (params.state || "").toLowerCase();
  const routeGrade = normalizeGrade(params.grade || "");

  const stateCode = routeState || (educator?.state || "").toLowerCase();
  const grade = routeGrade || normalizeGrade(educator?.grade || "");

  // -------------------------------------------------------------------------
  // Load educator, roster, and all mirrored progress
  // -------------------------------------------------------------------------

  const loadClassroom = useCallback(
    async (uid) => {
      setLoading(true);
      setLoadError("");

      try {
        const meSnap = await getDoc(doc(db, "users", uid));
        if (!meSnap.exists()) {
          setLoadError("We couldn't load your educator profile.");
          setLoading(false);
          return;
        }
        const me = { uid, ...meSnap.data() };
        setEducator(me);

        const code = me.educatorCode;
        if (!code) {
          setLoadError(
            "Your educator code hasn't been issued yet. Return to your dashboard and it will be created automatically."
          );
          setLoading(false);
          return;
        }

        // ROSTER — from educatorRosters/{code}/students, NOT from /users.
        //
        // This is the important one, and it's subtle enough to be worth
        // spelling out. The obvious query is:
        //
        //     query(collection(db, "users"), where("educatorCode", "==", code))
        //
        // That is a `list` on /users, and firestore.rules grants `list` on
        // /users to admins only. Worse, Firestore rules CANNOT inspect a
        // query's where-clause values, so there is no rule you can write that
        // means "you may list users, but only ones sharing your code" — the
        // permission isn't expressible at that shape. Every educator, trial
        // or paid, would get a permission error.
        //
        // utils/educatorCode.js already solved this: the student writes their
        // own entry to educatorRosters/{code}/students/{uid} at signup, and
        // that subcollection IS listable by whoever owns the code. No
        // elevated permission on either side.
        //
        // The old per-state pages also filtered on role == "va-3rd-student",
        // which returned nothing during a trial anyway — everyone's role is
        // "unpaid" until Stripe promotes them.
        const rosterEntries = await listRoster(code);

        // The roster row is a lightweight copy written at signup. Avatars and
        // later name edits live on the user document, so enrich where we're
        // permitted to read it — an educator can read the profile of someone
        // sharing their code. A refusal on any single student degrades to the
        // roster copy rather than dropping the child from the list.
        const roster = (
          await Promise.all(
            rosterEntries
              .filter((r) => r.uid !== uid)
              .filter((r) => !String(r.intendedRole || "").includes("educator"))
              .map(async (entry) => {
                try {
                  const snap = await getDoc(doc(db, "users", entry.uid));
                  return snap.exists()
                    ? { ...entry, ...snap.data(), uid: entry.uid }
                    : entry;
                } catch {
                  return entry;
                }
              })
          )
        ).sort((a, b) =>
          `${a.lastName || ""}${a.firstName || ""}`.localeCompare(
            `${b.lastName || ""}${b.firstName || ""}`
          )
        );

        setStudents(roster);

        // PROGRESS — every subject bucket under this educator, in one pass.
        //
        // collectionGroup over the mirror is a single query instead of one
        // read per student per subject. Falls back to per-student userData
        // reads if the rules don't allow the group query.
        const merged = {};

        try {
          const mirrorSnap = await getDocs(
            query(
              collectionGroup(db, "students"),
              where("educatorCode", "==", code)
            )
          );

          mirrorSnap.forEach((d) => {
            // .../subjects/{subjectKey}/students/{studentUid}
            const subjectKey = d.ref.parent.parent?.id || "";
            const row = d.data();
            const sUid = row.studentUid || d.id;
            merged[sUid] = merged[sUid] || {};
            merged[sUid][subjectKey] = {
              ...(merged[sUid][subjectKey] || {}),
              ...(row.slugs || {}),
            };
          });
        } catch (err) {
          console.warn(
            "[EducatorClassroom] mirror group query unavailable, falling back to userData:",
            err?.code || err?.message
          );
        }

        // Fill gaps from userData for students the mirror hasn't caught up on.
        await Promise.all(
          roster.map(async (student) => {
            if (merged[student.uid]) return;
            try {
              const snap = await getDoc(doc(db, "userData", student.uid));
              if (!snap.exists()) return;
              const data = snap.data() || {};
              const buckets = {};
              Object.keys(data).forEach((k) => {
                if (data[k] && typeof data[k] === "object" && k.includes("_")) {
                  buckets[k] = data[k];
                }
              });
              merged[student.uid] = buckets;
            } catch {
              /* a single unreadable student shouldn't blank the class */
            }
          })
        );

        setProgressByStudent(merged);
      } catch (err) {
        console.error("[EducatorClassroom] load failed:", err);
        setLoadError(
          "Something went wrong loading your classroom. Please refresh, or contact support at (571) 563-6142."
        );
      } finally {
        setLoading(false);
      }
    },
    [db]
  );

  // LIVE: when a student finishes a game, video, song or sing-along (or a
  // new student joins), the page updates by itself — no refresh.
  const code = educator?.educatorCode || "";
  const studentKey = students.map((x) => x.uid).sort().join(",");
  useEffect(() => {
    if (!code) return undefined;
    const stops = [];
    // new students joining the class list
    stops.push(
      onSnapshot(
        collection(db, "educatorRosters", String(code), "students"),
        (snap) => {
          const rows = snap.docs.map((d) => ({ id: d.id, uid: d.id, ...d.data() })).filter((r) => r.uid !== educator?.uid && !String(r.intendedRole || "").includes("educator"));
          setStudents((prev) => {
            const have = new Map(prev.map((x) => [x.uid, x]));
            const next = rows.map((r) => ({ ...r, ...(have.get(r.uid) || {}) }));
            const same = next.length === prev.length && next.every((x) => have.has(x.uid));
            return same ? prev : next.sort((a, b) => `${a.lastName || ""}${a.firstName || ""}`.localeCompare(`${b.lastName || ""}${b.firstName || ""}`));
          });
        },
        () => {}
      )
    );
    // each student's progress, as it happens
    (studentKey ? studentKey.split(",") : []).forEach((uid) => {
      stops.push(
        onSnapshot(
          doc(db, "userData", uid),
          (snap) => {
            if (!snap.exists()) return;
            const data = snap.data() || {};
            const buckets = {};
            Object.keys(data).forEach((k) => {
              if (data[k] && typeof data[k] === "object" && k.includes("_")) buckets[k] = data[k];
            });
            setProgressByStudent((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), ...buckets } }));
          },
          () => {} // not readable — the class mirror (loaded above) still shows
        )
      );
    });
    return () => stops.forEach((f) => f());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, studentKey, db]);

  // everyone starts as a portrait each time the page opens
  useEffect(() => {
    clearFocusedAvatar();
    return clearFocusedAvatar;
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) loadClassroom(u.uid);
      else setLoading(false);
    });
    return () => unsub();
  }, [auth, loadClassroom]);

  // -------------------------------------------------------------------------
  // Derived view data
  // -------------------------------------------------------------------------

  /** Picks this student's bucket for the selected subject, however it's keyed. */
  const bucketFor = useCallback(
    (uid) => {
      const buckets = progressByStudent[uid] || {};
      const keys = Object.keys(buckets);
      const exact = `${stateCode}_${grade}_${subject}`;
      if (buckets[exact]) return buckets[exact];

      const byGradeAndSubject = keys.find(
        (k) => k.endsWith(`_${subject}`) && k.includes(`_${grade}_`)
      );
      if (byGradeAndSubject) return buckets[byGradeAndSubject];

      const bySubject = keys.find((k) => k.endsWith(`_${subject}`));
      return bySubject ? buckets[bySubject] : {};
    },
    [progressByStudent, stateCode, grade, subject]
  );

  /** Roll-up per student for the summary table. */
  const rows = useMemo(
    () =>
      students.map((student) => {
        const bucket = bucketFor(student.uid);
        const slugs = Object.keys(bucket).filter(
          (k) => bucket[k] && typeof bucket[k] === "object"
        );

        let videoPlays = 0;
        let musicPlays = 0;
        let gamesPlayed = 0;
        let bestTotal = 0;
        let scoreSum = 0;
        let scoreCount = 0;
        let lastActive = null;

        slugs.forEach((slug) => {
          const t = bucket[slug] || {};
          const g = t.game || {};

          videoPlays += Number(t.videoQty || 0) || (t.video ? 1 : 0);
          musicPlays += Number(t.musicQty || 0) || (t.music ? 1 : 0);

          const plays =
            Number(g.plays || g.attempts || 0) ||
            (Array.isArray(g.score) ? g.score.length : 0) ||
            (t.game ? 1 : 0);
          gamesPlayed += plays;

          const total =
            typeof g.latestTotal === "number"
              ? g.latestTotal
              : Array.isArray(g.score) && g.score.length
              ? g.score[g.score.length - 1]
              : null;

          if (typeof total === "number") {
            bestTotal = Math.max(bestTotal, total);
            scoreSum += total;
            scoreCount += 1;
          }

          const stamps = [t.lastVideoAt, t.lastMusicAt, g.lastPlayedAt]
            .map((x) => (x?.toDate ? x.toDate() : x ? new Date(x) : null))
            .filter(Boolean);
          stamps.forEach((d) => {
            if (!lastActive || d > lastActive) lastActive = d;
          });
        });

        return {
          student,
          topicsTouched: slugs.length,
          videoPlays,
          musicPlays,
          gamesPlayed,
          bestTotal,
          avgTotal: scoreCount ? Math.round(scoreSum / scoreCount) : null,
          lastActive,
        };
      }),
    [students, bucketFor]
  );

  const classTotals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          videoPlays: acc.videoPlays + r.videoPlays,
          musicPlays: acc.musicPlays + r.musicPlays,
          gamesPlayed: acc.gamesPlayed + r.gamesPlayed,
          active: acc.active + (r.topicsTouched > 0 ? 1 : 0),
        }),
        { videoPlays: 0, musicPlays: 0, gamesPlayed: 0, active: 0 }
      ),
    [rows]
  );

  const selectedRow = rows.find((r) => r.student.uid === selectedUid) || null;

  const handleHome = () => navigate("/educator/dashboard");

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.shell}>
          <div style={s.skeletonBar} />
          <div style={{ ...s.skeletonBar, width: "60%" }} />
          <div style={{ ...s.skeletonBar, width: "80%" }} />
          <p style={s.muted}>Loading your classroom…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={s.page}>
        <div style={s.shell}>
          <div style={s.errorBox}>{loadError}</div>
          <button style={s.secondaryBtn} onClick={handleHome}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const stateLabel = STATE_NAMES[stateCode] || stateCode.toUpperCase();
  const gradeLabel = grade ? `${titleCase(grade)} Grade` : "";

  const subjectLabel = SUBJECTS.find((x) => x.key === subject)?.label || "";
  const shortName = (st) => `${st.firstName || "Student"}${st.lastName ? ` ${st.lastName.charAt(0)}.` : ""}`;
  const togglePick = (uid) => setPicked((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));
  const openDetail = (uid) => {
    setSelectedUid(selectedUid === uid ? null : uid);
    setTimeout(() => document.getElementById("cr-detail")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  return (
    <div className="cr-page">
      <div className="cr-shell">
        <div className="cr-topbar">
          <button type="button" className="cr-back" onClick={handleHome}>
            ← Dashboard
          </button>
          <span className="cr-topbar-coins">
            <CoinCounter />
            <BuyCoinsButton educator />
          </span>
        </div>

        {/* ---------------- Header ---------------- */}
        <header className="cr-hero">
          <div className="cr-hero-text">
            <p className="cr-eyebrow">👩‍🏫 My Classroom</p>
            <h1>{formalName(educator || {}, "My")}'s class</h1>
            <p className="cr-sub">
              {[gradeLabel, stateLabel].filter(Boolean).join(" · ")}
              {educator?.schoolName ? ` · ${educator.schoolName}` : ""}
            </p>
          </div>
          <div className="cr-code">
            <span className="cr-code-label">Class code</span>
            <span className="cr-code-value">{educator?.educatorCode || "—"}</span>
            <CopyBtn text={educator?.educatorCode} />
            <span className="cr-code-hint">Students type this code when they sign up — then they appear here.</span>
          </div>
        </header>

        <ClassroomGuide />

        {/* ---------------- Class summary ---------------- */}
        <section className="cr-stats" aria-label="Class totals">
          <Stat icon="🧒" tone="violet" label="Students" value={students.length} />
          <Stat icon="⚡" tone="green" label={`Active in ${subjectLabel}`} value={classTotals.active} />
          <Stat icon="🎬" tone="blue" label="Video plays" value={classTotals.videoPlays} />
          <Stat icon="🎵" tone="pink" label="Song plays" value={classTotals.musicPlays} />
          <Stat icon="🎮" tone="orange" label="Games played" value={classTotals.gamesPlayed} />
        </section>

        {students.length > 0 && <ClassGiveCoins students={students} selected={picked} onClear={() => setPicked([])} />}

        {/* ---------------- Students ---------------- */}
        <section className="cr-section">
          <div className="cr-sec-head">
            <h2>Students {students.length ? <span className="cr-count">{students.length}</span> : null}</h2>
            <nav className="cr-subjects" aria-label="Show progress in">
              <span>Progress in:</span>
              {SUBJECTS.map((sub) => (
                <button key={sub.key} type="button" className={subject === sub.key ? "is-on" : ""} aria-pressed={subject === sub.key} onClick={() => setSubject(sub.key)}>
                  <span aria-hidden="true">{SUBJECT_ICONS[sub.key]}</span> {sub.label}
                </button>
              ))}
            </nav>
          </div>

          {students.length === 0 ? (
            <div className="cr-empty">
              <span className="cr-empty-icon" aria-hidden="true">🎒</span>
              <h3>No students yet</h3>
              <p>
                Share your class code <strong>{educator?.educatorCode}</strong> with your class. Students type it when they create their account, and they'll appear here
                automatically.
              </p>
            </div>
          ) : (
            <>
              <p className="cr-hint">
                👆 Tap an <b>avatar</b> to see the whole outfit · <b>See progress</b> shows each topic · tick <b>☑</b> to choose who gets coins
              </p>
              <div className="cr-grid">
                {rows.map((r) => {
                  const st = r.student;
                  const isPicked = picked.includes(st.uid);
                  return (
                    <article key={st.uid} className={`cr-card ${isPicked ? "is-picked" : ""} ${selectedUid === st.uid ? "is-open" : ""}`}>
                      <label className="cr-pick" title={isPicked ? "Ticked for Give coins" : "Tick to give coins"}>
                        <input type="checkbox" checked={isPicked} onChange={() => togglePick(st.uid)} aria-label={`Choose ${shortName(st)} for coins`} />
                      </label>
                      <div className="cr-card-av">
                        <FocusAvatar id={st.uid} size={88} fullSize={196} name={st.firstName || ""} />
                      </div>
                      <h3>{shortName(st)}</h3>
                      <p className={`cr-last ${r.lastActive || r.topicsTouched ? "" : "is-new"}`}>
                        {r.lastActive
                          ? `Last active ${r.lastActive.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                          : r.topicsTouched
                          ? `${r.topicsTouched} ${subjectLabel} topic${r.topicsTouched === 1 ? "" : "s"} started`
                          : `Not started ${subjectLabel} yet`}
                      </p>
                      <div className="cr-mini">
                        <span title="Topics started">
                          📚 <b>{r.topicsTouched}</b>
                        </span>
                        <span title="Games played">
                          🎮 <b>{r.gamesPlayed}</b>
                        </span>
                        <span title="Average game score">
                          ⭐ <b>{r.avgTotal === null ? "—" : r.avgTotal}</b>
                        </span>
                      </div>
                      <button type="button" className="cr-see" onClick={() => openDetail(st.uid)}>
                        {selectedUid === st.uid ? "Hide progress" : "See progress"}
                      </button>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </section>

        {/* ---------------- Per-topic detail ---------------- */}
        {selectedRow && (
          <div id="cr-detail">
            <TopicDetail row={selectedRow} bucket={bucketFor(selectedRow.student.uid)} subjectLabel={subjectLabel} onClose={() => setSelectedUid(null)} onOpenBreakdown={setBreakdown} />
          </div>
        )}

        {/* ---------------- Gradebook ---------------- */}
        {students.length > 0 && (
          <section className="cr-section">
            <div className="cr-sec-head">
              <h2>📒 Gradebook — {subjectLabel}</h2>
            </div>
            <p className="cr-hint">Every student on one page. Click a row to see that student's topics. A dash (—) means nothing yet.</p>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Student</th>
                    <th style={s.thNum}>Topics</th>
                    <th style={s.thNum}>Videos</th>
                    <th style={s.thNum}>Songs</th>
                    <th style={s.thNum}>Games</th>
                    <th style={s.thNum}>Avg score</th>
                    <th style={s.thNum}>Best</th>
                    <th style={s.th}>Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.student.uid} onClick={() => openDetail(r.student.uid)} style={{ ...s.tr, ...(selectedUid === r.student.uid ? s.trActive : null) }}>
                      <td style={s.tdName}>
                        <span>{shortName(r.student)}</span>
                      </td>
                      <td style={s.tdNum}>{r.topicsTouched || "—"}</td>
                      <td style={s.tdNum}>{r.videoPlays || "—"}</td>
                      <td style={s.tdNum}>{r.musicPlays || "—"}</td>
                      <td style={s.tdNum}>{r.gamesPlayed || "—"}</td>
                      <td style={s.tdNum}>{r.avgTotal === null ? "—" : r.avgTotal}</td>
                      <td style={s.tdNum}>{r.bestTotal || "—"}</td>
                      <td style={s.td}>{r.lastActive ? r.lastActive.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {breakdown && <BreakdownModal data={breakdown} onClose={() => setBreakdown(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Stat({ label, value, icon, tone }) {
  return (
    <div className={`cr-stat is-${tone}`}>
      <span className="cr-stat-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="cr-stat-value">{value}</span>
      <span className="cr-stat-label">{label}</span>
    </div>
  );
}

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      className="cr-copy"
      onClick={() => {
        try {
          navigator.clipboard.writeText(String(text));
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* no clipboard */
        }
      }}
    >
      {done ? "Copied ✓" : "Copy"}
    </button>
  );
}

const GUIDE_KEY = "sbl-classroom-guide";
function ClassroomGuide() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(GUIDE_KEY) !== "hidden";
    } catch {
      return true;
    }
  });
  const flip = () => {
    setOpen((o) => {
      try {
        localStorage.setItem(GUIDE_KEY, o ? "hidden" : "shown");
      } catch {
        /* ignore */
      }
      return !o;
    });
  };
  return (
    <section className={`cr-guide ${open ? "" : "is-closed"}`}>
      <div className="cr-guide-head">
        <h2>💡 How this page works</h2>
        <button type="button" onClick={flip}>
          {open ? "Hide" : "Show"}
        </button>
      </div>
      {open && (
        <ol className="cr-guide-steps">
          <li>
            <span aria-hidden="true">🔑</span>
            <b>Share your class code</b>
            <small>Students type it when they sign up. They show up below by themselves.</small>
          </li>
          <li>
            <span aria-hidden="true">📚</span>
            <b>Pick a subject</b>
            <small>Math, Science or Social Studies — the cards, totals and gradebook switch to it.</small>
          </li>
          <li>
            <span aria-hidden="true">👀</span>
            <b>Check progress</b>
            <small>“See progress” lists every topic: video and song plays, quiz and bonus scores, and their answers.</small>
          </li>
          <li>
            <span aria-hidden="true">🎁</span>
            <b>Give coins</b>
            <small>Whole class or ticked students, the same amount each or a total shared out — from your class coin bank or free daily gifts.</small>
          </li>
        </ol>
      )}
    </section>
  );
}

function TopicDetail({ row, bucket, subjectLabel, onClose, onOpenBreakdown }) {
  const slugs = Object.keys(bucket)
    .filter((k) => bucket[k] && typeof bucket[k] === "object")
    .sort();

  return (
    <section style={s.detail}>
      <div style={s.detailHead}>
        <div style={s.detailWho}>
          <FocusAvatar id={`detail-${row.student.uid}`} uid={row.student.uid} size={56} fullSize={150} name={row.student.firstName || ""} />
          <div>
            <h2 style={s.detailName}>
              {row.student.firstName} {row.student.lastName}
            </h2>
            <p style={s.detailMeta}>
              {subjectLabel} · {slugs.length} topic
              {slugs.length === 1 ? "" : "s"} started
            </p>
          </div>
        </div>
        <button style={s.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      {slugs.length === 0 ? (
        <p style={s.muted}>
          No {subjectLabel.toLowerCase()} activity recorded yet.
        </p>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Topic</th>
                <th style={s.thNum}>Video plays</th>
                <th style={s.thNum}>Song plays</th>
                <th style={s.thNum}>Quiz</th>
                <th style={s.thNum}>Bonus</th>
                <th style={s.thNum}>Total</th>
                <th style={s.th} />
              </tr>
            </thead>
            <tbody>
              {slugs.map((slug) => {
                const t = bucket[slug] || {};
                const g = t.game || {};

                const quiz =
                  typeof g.latestQuiz === "number"
                    ? g.latestQuiz
                    : typeof g.quizScore === "number"
                    ? g.quizScore
                    : null;

                const total =
                  typeof g.latestTotal === "number"
                    ? g.latestTotal
                    : Array.isArray(g.score) && g.score.length
                    ? g.score[g.score.length - 1]
                    : null;

                const bonus =
                  typeof g.latestBonus === "number"
                    ? g.latestBonus
                    : typeof g.bonusScore === "number"
                    ? g.bonusScore
                    : quiz !== null && total !== null
                    ? total - quiz
                    : null;

                const answers = g.latestDetail?.answers || [];

                return (
                  <tr key={slug} style={s.tr}>
                    <td style={s.td}>{titleCase(slug)}</td>
                    <td style={s.tdNum}>
                      {t.videoQty || (t.video ? 1 : 0) || "—"}
                    </td>
                    <td style={s.tdNum}>
                      {t.musicQty || (t.music ? 1 : 0) || "—"}
                    </td>
                    <td style={s.tdNum}>{quiz === null ? "—" : quiz}</td>
                    <td style={s.tdNum}>{bonus === null ? "—" : bonus}</td>
                    <td style={{ ...s.tdNum, fontWeight: 700 }}>
                      {total === null ? "—" : total}
                    </td>
                    <td style={s.td}>
                      {answers.length > 0 && (
                        <button
                          style={s.linkBtn}
                          onClick={() =>
                            onOpenBreakdown({
                              title: `${titleCase(slug)} — ${row.student.firstName}`,
                              answers,
                            })
                          }
                        >
                          View answers
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BreakdownModal({ data, onClose }) {
  const correct = data.answers.filter((a) => a.correct || a.isCorrect).length;

  return (
    <div style={s.overlay} onClick={onClose} role="presentation">
      <div
        style={s.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={s.modalHead}>
          <h3 style={s.modalTitle}>{data.title}</h3>
          <button style={s.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p style={s.modalScore}>
          {correct} of {data.answers.length} correct
        </p>

        <ol style={s.answerList}>
          {data.answers.map((a, i) => {
            const ok = a.correct || a.isCorrect;
            return (
              <li key={i} style={s.answerItem}>
                <span style={ok ? s.badgeOk : s.badgeNo}>{ok ? "✓" : "✗"}</span>
                <div>
                  <p style={s.qText}>{a.question || a.prompt || `Question ${i + 1}`}</p>
                  <p style={s.aText}>
                    Answered: {a.selected || a.answer || "—"}
                    {!ok && (a.correctAnswer || a.expected)
                      ? ` · Correct: ${a.correctAnswer || a.expected}`
                      : ""}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
//
// Restrained on purpose. The old pages used hsl(index*40, 70%, 80%) buttons —
// a different pastel per student — which reads as a toy next to the gradebook
// software a teacher already uses. One accent colour, real type scale,
// generous whitespace.
// ---------------------------------------------------------------------------

const INK = "#0f172a";
const MUTED = "#64748b";
const LINE = "#e2e8f0";
const ACCENT = "#1d4ed8";

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8fafc",
    padding: "28px 20px 64px",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    color: INK,
  },
  shell: { maxWidth: 1180, margin: "0 auto" },

  header: {
    display: "flex",
    flexWrap: "wrap",
    gap: 20,
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  headerLeft: { minWidth: 260 },
  eyebrow: {
    margin: 0,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: MUTED,
  },
  h1: {
    margin: "4px 0 6px",
    fontSize: "clamp(26px, 4vw, 36px)",
    fontWeight: 800,
    lineHeight: 1.15,
  },
  headerSub: { margin: 0, color: MUTED, fontSize: 15 },

  codeCard: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    padding: "14px 20px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },
  codeLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: MUTED,
  },
  codeValue: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "0.04em",
    fontVariantNumeric: "tabular-nums",
  },
  codeHint: { fontSize: 12, color: MUTED },

  statRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: 12,
    margin: "22px 0",
  },
  stat: {
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  statValue: { fontSize: 26, fontWeight: 800, fontVariantNumeric: "tabular-nums" },
  statLabel: { fontSize: 12, color: MUTED, fontWeight: 600 },

  tabs: {
    display: "flex",
    gap: 6,
    borderBottom: `1px solid ${LINE}`,
    marginBottom: 18,
    flexWrap: "wrap",
  },
  tab: {
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: "10px 14px",
    fontSize: 14,
    fontWeight: 600,
    color: MUTED,
    cursor: "pointer",
  },
  tabActive: { color: ACCENT, borderBottomColor: ACCENT },

  rosterStrip: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  rosterChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 999,
    padding: "5px 14px 5px 5px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    color: INK,
  },
  rosterChipActive: {
    borderColor: ACCENT,
    boxShadow: `0 0 0 2px ${ACCENT}22`,
  },
  chipName: { whiteSpace: "nowrap" },

  avatarImg: { borderRadius: "50%", objectFit: "cover", display: "block" },
  avatarFallback: {
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    flexShrink: 0,
  },

  tableWrap: {
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    overflowX: "auto",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: MUTED,
    borderBottom: `1px solid ${LINE}`,
    whiteSpace: "nowrap",
  },
  thNum: {
    textAlign: "right",
    padding: "12px 16px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: MUTED,
    borderBottom: `1px solid ${LINE}`,
    whiteSpace: "nowrap",
  },
  tr: { cursor: "pointer", borderBottom: `1px solid ${LINE}` },
  trActive: { background: "#eff6ff" },
  td: { padding: "12px 16px", color: INK },
  tdNum: {
    padding: "12px 16px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    color: INK,
  },
  tdName: {
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 600,
  },

  detail: {
    marginTop: 24,
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    padding: 22,
  },
  detailHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 18,
  },
  detailWho: { display: "flex", gap: 14, alignItems: "center" },
  detailName: { margin: 0, fontSize: 20, fontWeight: 700 },
  detailMeta: { margin: "2px 0 0", color: MUTED, fontSize: 14 },

  linkBtn: {
    background: "none",
    border: "none",
    color: ACCENT,
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    padding: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 26,
    lineHeight: 1,
    color: MUTED,
    cursor: "pointer",
    padding: 4,
  },
  secondaryBtn: {
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 8,
    padding: "10px 18px",
    fontWeight: 600,
    cursor: "pointer",
  },

  emptyState: {
    background: "#fff",
    border: `1px dashed ${LINE}`,
    borderRadius: 12,
    padding: "44px 28px",
    textAlign: "center",
  },
  emptyTitle: { margin: "0 0 8px", fontSize: 20, fontWeight: 700 },
  emptyBody: { margin: 0, color: MUTED, maxWidth: 460, marginInline: "auto" },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    padding: 24,
    width: "100%",
    maxWidth: 620,
    maxHeight: "85vh",
    overflowY: "auto",
  },
  modalHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  modalScore: { margin: "4px 0 16px", color: MUTED, fontWeight: 600 },
  answerList: { listStyle: "none", margin: 0, padding: 0 },
  answerItem: {
    display: "flex",
    gap: 12,
    padding: "12px 0",
    borderTop: `1px solid ${LINE}`,
  },
  badgeOk: {
    color: "#15803d",
    fontWeight: 800,
    flexShrink: 0,
    width: 20,
    textAlign: "center",
  },
  badgeNo: {
    color: "#b91c1c",
    fontWeight: 800,
    flexShrink: 0,
    width: 20,
    textAlign: "center",
  },
  qText: { margin: 0, fontWeight: 600, fontSize: 14 },
  aText: { margin: "3px 0 0", color: MUTED, fontSize: 13 },

  errorBox: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    borderRadius: 10,
    padding: "16px 20px",
    marginBottom: 16,
  },
  muted: { color: MUTED, fontSize: 14 },
  skeletonBar: {
    height: 18,
    background: "#e2e8f0",
    borderRadius: 6,
    margin: "10px 0",
  },
};
